import type { DiscoveryFilters } from '../types/discoveryBadges';

const STRING_KEYS: (keyof DiscoveryFilters)[] = [
  'make',
  'model',
  'trim',
  'color',
  'chassis',
  'engine',
  'transmission',
  'drivetrain',
  'build_status',
  'exterior_finish_type',
  'exterior_finish_color',
  'exterior_finish_brand',
  'mod_brand',
  'mod_category',
  'event_slug',
  'award_title',
  'badge_category',
  'club_slug',
  'spec_key',
  'spec_value',
  'build_purpose',
  'achievement',
];

const FLAG_KEYS: (keyof DiscoveryFilters)[] = [
  'has_images',
  'has_modifications',
  'has_awards',
  'verified_club',
  'certified',
  'has_verified_dyno',
  'has_performance_record',
  'has_achievement',
  'has_shop_contributor',
  'has_albums',
];

const RANGE_KEYS: (keyof DiscoveryFilters)[] = [
  'year_min',
  'year_max',
  'doc_score_min',
  'power_min',
  'power_max',
  'ptw_min',
];

export function filtersFromParams(params: URLSearchParams): DiscoveryFilters {
  const get = (k: string) => params.get(k) || undefined;
  const filters: DiscoveryFilters = {
    search: get('q') || get('search'),
    sort_by: get('sort_by') || 'updated_at',
    sort_order: (get('sort_order') as 'asc' | 'desc') || 'desc',
    page: Number(get('page') || 1),
    limit: 20,
  };

  for (const key of STRING_KEYS) {
    const v = get(String(key));
    if (v) filters[key] = v as never;
  }
  for (const key of FLAG_KEYS) {
    const v = get(String(key));
    if (v) filters[key] = v as never;
  }
  for (const key of RANGE_KEYS) {
    const v = get(String(key));
    if (v) filters[key] = v as never;
  }

  return filters;
}

export function filtersToSearchParams(filters: DiscoveryFilters): URLSearchParams {
  const next = new URLSearchParams();
  const map: Record<string, string | undefined> = {
    q: filters.search ? String(filters.search) : undefined,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    page: filters.page && filters.page > 1 ? String(filters.page) : undefined,
  };

  for (const key of STRING_KEYS) {
    const v = filters[key];
    map[String(key)] = v != null && v !== '' ? String(v) : undefined;
  }
  for (const key of FLAG_KEYS) {
    const v = filters[key];
    map[String(key)] = v ? String(v) : undefined;
  }
  for (const key of RANGE_KEYS) {
    const v = filters[key];
    map[String(key)] = v != null && v !== '' ? String(v) : undefined;
  }

  Object.entries(map).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== 'false') next.set(k, v);
  });
  return next;
}

/** Count of user-facing filter dimensions currently applied (excludes sort/page/search). */
export function countActiveDiscoveryFilters(filters: DiscoveryFilters): number {
  let n = 0;
  for (const key of [...STRING_KEYS, ...FLAG_KEYS, ...RANGE_KEYS]) {
    const v = filters[key];
    if (v !== undefined && v !== null && v !== '' && v !== false && v !== 'false') n += 1;
  }
  // year_min+year_max count as one chip but two keys — keep raw count for badge accuracy
  return n;
}
